import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../../sporttia/dialogs/SttDialog';
import { AppContext } from '../../../sporttia/AppContext';
import { SttSelectableItems } from '../../../sporttia/SttSelectableItems';
import { getErrorMessage } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    attendance?: Attendance | null;
    attendanceDay?: string | null;
    open: boolean;
    onClose: () => void;
};

type Attendance = {
    id: number;
    name?: string;
    assistences: Assistance[];
};

type Assistance = {
    id: number;
    class: {
        id: number;
        ini: string;
        end: string;
    };
    status: string;
    notes?: string;
};

type AttendanceDay = {
    id: string;
    status: string;
    class: {
        id: number;
        ini: string;
        end: string;
    };
    notes: string;
};

const defaultAttendanceDayChosen: AttendanceDay = {
    id: '',
    status: 'ASSIST',
    class: {
        id: 0,
        ini: '',
        end: '',
    },
    notes: '',
};

export default function AttendanceDialog({
    attendance = null,
    attendanceDay = null,
    open,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [attendanceChosen, setAttendanceChosen] = useState<Attendance | null>(
        attendance,
    );
    const [attendanceDayChosen, setAttendanceDayChosen] =
        useState<AttendanceDay>(defaultAttendanceDayChosen);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (attendance && attendanceDay) {
            setLoading(true);
            const assistanceID = attendance.assistences.find(
                (assistance) =>
                    new Date(assistance.class.ini).getDate() ===
                    parseInt(attendanceDay),
            )?.id;
            if (assistanceID) {
                cxt?.api('GET', `/classes/assistences/${assistanceID}`, {
                    success: (result: { classAssistence?: Assistance }) => {
                        if (result.classAssistence) {
                            setAttendanceDayChosen(
                                result.classAssistence as unknown as AttendanceDay,
                            );
                        }
                        setLoading(false);
                    },
                    error: () => {
                        setLoading(false);
                    },
                });
            } else {
                setAttendanceDayChosen(defaultAttendanceDayChosen);
                setLoading(false);
            }
            setAttendanceChosen(attendance);
        }
    }, [attendance, attendanceDay]);

    const saveAttendance = (): void => {
        setSaving(true);
        if (attendanceDayChosen.id) {
            cxt?.api('PUT', `/classes/assistences/${attendanceDayChosen.id}`, {
                params: {
                    status: attendanceDayChosen.status,
                    notes: attendanceDayChosen.notes,
                },
                success: () => {
                    setSaving(false);
                    onClose();
                },
                error: (errorResponse: unknown) => {
                    setSaving(false);
                    cxt.showMessage('E', getErrorMessage(errorResponse));
                },
            });
        } else {
            const classId = attendance?.assistences.find(
                (assistance) =>
                    new Date(assistance.class.ini).getDate() ===
                    parseInt(attendanceDay!),
            )?.class.id;
            cxt?.api('POST', `/classes/${classId}/assistences`, {
                params: {
                    idStudent: attendanceChosen?.id,
                    status: attendanceDayChosen.status,
                    notes: attendanceDayChosen.notes,
                },
                success: () => {
                    setSaving(false);
                    onClose();
                },
                error: (errorResponse: unknown) => {
                    setSaving(false);
                    cxt.showMessage('E', getErrorMessage(errorResponse));
                },
            });
        }
    };

    const dialogContent = loading ? (
        <Box className="flex justify-center items-center py-8">
            <CircularProgress />
        </Box>
    ) : (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                    {t('User')}
                </Typography>
                <Typography variant="h6" className="font-medium">
                    {attendance?.name || '-'}
                </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <SttSelectableItems
                    caption={t('Status')}
                    name="status"
                    items={[
                        {
                            label: t('CAS.ASSIST'),
                            value: 'ASSIST',
                        },
                        {
                            label: t('CAS.MISSED'),
                            value: 'MISSED',
                        },
                        {
                            label: t('CAS.RECOVERABLE'),
                            value: 'RECOVERABLE',
                        },
                    ]}
                    exclusive
                    selected={attendanceDayChosen.status}
                    onChange={({
                        name,
                        value,
                    }: {
                        name?: string;
                        value: unknown;
                    }) => {
                        if (!name) return;
                        setAttendanceDayChosen({
                            ...attendanceDayChosen,
                            [name]: value,
                        });
                    }}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    label={t('Notes')}
                    size="small"
                    value={attendanceDayChosen.notes || ''}
                    onChange={(e) =>
                        setAttendanceDayChosen({
                            ...attendanceDayChosen,
                            notes: e.target.value,
                        })
                    }
                />
            </Grid>
        </Grid>
    );

    return (
        <SttDialog
            title={t('Assistence')}
            open={open}
            onClose={onClose}
            maxWidth="sm"
            content={dialogContent}
            buttons={[
                {
                    type: 'save',
                    onClick: saveAttendance,
                    permission: PERMISSIONS.ACTIVITY.MODIFY,
                    loading: saving,
                    disabled: saving,
                    show: !loading,
                },
            ]}
        />
    );
}
